import {Action, State, StateContext} from '@ngxs/store';
import {inject, Injectable} from '@angular/core';
import {
    AbstractBulkOperationState,
    BulkOperationOptions,
} from '../../modules/shared/components/abstract/bulk-operation-state/abstract-bulk-operation.state';
import {ValidationStateModel} from '../../modules/validation/models/validation-state.model';
import {GeneralContractVerificationActions} from './general-contract-verification.actions';
import {BulkOperation} from '../../models/api/bulk-operation.model';
import {Observable, of, switchMap} from 'rxjs';
import {DatatableBulkPayload} from '../../models/datatable.model';
import {BulkConfirmModalComponent} from '../../modules/shared/components/bulk-confirm-modal';
import {SearchListEnum} from '../../enums/search-list.enum';
import {GeneralContractBosCacheService} from '../../services/general-contract-bos-cache.service';
import {filter, tap} from 'rxjs/operators';
import {SearchListActions} from '../search-list/search-list.actions';

type GeneralContractVerificationStateModel = ValidationStateModel;

@State<GeneralContractVerificationStateModel>({
    name: GeneralContractVerificationState.STATE_NAME,
    defaults: {
        validationErrors: null,
    },
})
@Injectable()
export class GeneralContractVerificationState extends AbstractBulkOperationState {
    static readonly STATE_NAME = 'generalContractVerification';

    protected generalContractBosCacheService = inject(GeneralContractBosCacheService);

    @Action(GeneralContractVerificationActions.Verify)
    verify(
        ctx: StateContext<GeneralContractVerificationStateModel>,
        {item}: GeneralContractVerificationActions.Verify
    ) {
        return this.modalService
            .confirm({
                nzTitle: this.translate.instant('GENERAL_CONTRACT_VERIFICATION.VERIFY_TITLE'),
                nzContent: this.translate.instant('GENERAL_CONTRACT_VERIFICATION.VERIFY_CONFIRMATION', {
                    number: item.number,
                }),
                nzOnOk: () => true,
                nzOnCancel: () => null,
            })
            .afterClose.pipe(
                filter(Boolean),
                switchMap(() =>
                    this.generalContractBosCacheService.verify(item.id).pipe(
                        tap(() =>
                            this.notificationService.success(
                                this.translate.instant(`GENERAL_CONTRACT_VERIFICATION.VERIFY_SUCCESS`, {
                                    number: item.generalContract.number,
                                })
                            )
                        ),
                        switchMap(() =>
                            ctx.dispatch(new SearchListActions.GetListPage(SearchListEnum.GENERAL_CONTRACT_BOS_CACHE))
                        )
                    )
                )
            );
    }

    @Action(GeneralContractVerificationActions.BulkVerify)
    bulkVerify(_, {payload}: GeneralContractVerificationActions.BulkVerify) {
        const request = (item: BulkOperation) => this.generalContractBosCacheService.bulkVerify(item);
        const options: BulkOperationOptions = {
            confirmTitle: 'GENERAL_CONTRACT_VERIFICATION.BULK_VERIFY_TITLE',
            confirmMessage: 'GENERAL_CONTRACT_VERIFICATION.BULK_VERIFY_TEXT',
            summaryTitle: 'GENERAL_CONTRACT_VERIFICATION.BULK_VERIFY_TITLE',
            summaryType: 'GENERAL_CONTRACT_VERIFICATION.BULK_TYPE',
            searchListReload: SearchListEnum.GENERAL_CONTRACT_BOS_CACHE,
        };
        return this.bulkOperationFlow(request, payload, options);
    }

    @Action(GeneralContractVerificationActions.Delete)
    delete(
        ctx: StateContext<GeneralContractVerificationStateModel>,
        {item}: GeneralContractVerificationActions.Delete
    ) {
        return this.modalService
            .confirm({
                nzTitle: this.translate.instant('GENERAL_CONTRACT_VERIFICATION.DELETE_TITLE'),
                nzContent: this.translate.instant('GENERAL_CONTRACT_VERIFICATION.DELETE_CONFIRMATION', {
                    number: item.number,
                }),
                nzOnOk: () => true,
                nzOnCancel: () => null,
            })
            .afterClose.pipe(
                filter(Boolean),
                switchMap(() =>
                    this.generalContractBosCacheService.delete(item.id).pipe(
                        tap(() =>
                            this.notificationService.success(
                                this.translate.instant(`GENERAL_CONTRACT_VERIFICATION.DELETE_SUCCESS`, {
                                    number: item.generalContract.number,
                                })
                            )
                        ),
                        switchMap(() =>
                            ctx.dispatch(new SearchListActions.GetListPage(SearchListEnum.GENERAL_CONTRACT_BOS_CACHE))
                        )
                    )
                )
            );
    }

    @Action(GeneralContractVerificationActions.BulkDelete)
    bulkDelete(_, {payload}: GeneralContractVerificationActions.BulkDelete) {
        const request = (item: BulkOperation) => this.generalContractBosCacheService.bulkDelete(item);
        const options: BulkOperationOptions = {
            confirmTitle: 'GENERAL_CONTRACT_VERIFICATION.BULK_DELETE_TITLE',
            confirmMessage: 'GENERAL_CONTRACT_VERIFICATION.BULK_DELETE_TEXT',
            summaryTitle: 'GENERAL_CONTRACT_VERIFICATION.BULK_DELETE_TITLE',
            summaryType: 'GENERAL_CONTRACT_VERIFICATION.BULK_TYPE',
            searchListReload: SearchListEnum.GENERAL_CONTRACT_BOS_CACHE,
        };
        return this.bulkOperationFlow(request, payload, options);
    }

    protected bulkOperationFlow(
        request: (operation: BulkOperation) => Observable<any>,
        initialPayload: Partial<DatatableBulkPayload>,
        options: BulkOperationOptions
    ): Observable<any> {
        return this.openBulkConfirmModal(initialPayload, options).pipe(
            switchMap((payload) => (payload ? this.bulkOperationRetryFlow(request, payload, options) : of(null)))
        );
    }

    protected openBulkConfirmModal(
        payload: Partial<DatatableBulkPayload>,
        options: BulkOperationOptions
    ): Observable<BulkOperation | undefined> {
        return this.zone.run(
            () =>
                this.modalService.generic({
                    nzContent: BulkConfirmModalComponent,
                    nzTitle: this.translate.instant(options.confirmTitle),
                    nzWidth: '70%',
                    nzCancelText: this.translate.instant('LABELS.CANCEL'),
                    nzOkText: this.translate.instant('LABELS.CONFIRM'),
                    nzData: {
                        payload: {
                            ...payload,
                        },
                        message: options.confirmMessage,
                    },
                    nzOnOk: () => payload,
                }).afterClose
        );
    }
}
